"use client";

import React from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { ArrowRight, TrendingUp, Users, Target } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
const dataGrowth = [{
  month: "Jan",
  value: 120
}, {
  month: "Feb",
  value: 180
}, {
  month: "Mar",
  value: 250
}, {
  month: "Apr",
  value: 310
}, {
  month: "May",
  value: 480
}, {
  month: "Jun",
  value: 600
}];
const dataReach = [{
  month: "Q1",
  value: 15000
}, {
  month: "Q2",
  value: 28000
}, {
  month: "Q3",
  value: 45000
}, {
  month: "Q4",
  value: 85000
}];
const dataEngagement = [{
  month: "S1",
  value: 45
}, {
  month: "S2",
  value: 52
}, {
  month: "S3",
  value: 68
}, {
  month: "S4",
  value: 74
}, {
  month: "S5",
  value: 92
}];
const configGrowth: ChartConfig = {
  value: {
    label: "Leads",
    color: "var(--chart-1)"
  }
};
const configReach: ChartConfig = {
  value: {
    label: "Portée",
    color: "var(--chart-1)"
  }
};
const configEngagement: ChartConfig = {
  value: {
    label: "Engagement",
    color: "var(--chart-3)"
  }
};
const caseStudies = [{
  title: "Lancement Agri-Tech 2024",
  client: "InnovAgri BF",
  description: "Stratégie digitale intégrée pour le lancement d'une nouvelle solution agraire.",
  metric: "+150%",
  metricLabel: "de Leads Qualifiés",
  icon: Target,
  chartType: "area",
  chartData: dataGrowth,
  chartConfig: configGrowth
}, {
  title: "Sommet Mining West Africa",
  client: "Chambre des Mines",
  description: "Gestion événementielle et relations presse pour le plus grand sommet minier.",
  metric: "3M+",
  metricLabel: "Portée Médiatique",
  icon: Users,
  chartType: "bar",
  chartData: dataReach,
  chartConfig: configReach
}, {
  title: "Rebranding Banque Atlantique",
  client: "Groupe Atlantique",
  description: "Refonte de l'identité visuelle et campagne de communication 360°.",
  metric: "+85%",
  metricLabel: "Engagement Digital",
  icon: TrendingUp,
  chartType: "area",
  chartData: dataEngagement,
  chartConfig: configEngagement
}];
export default function AnalyzedCaseStudies() {
  return <section id="études-de-cas-analysées" className="dark bg-background text-foreground py-20 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        {}
        <div className="mb-16 md:mb-24 max-w-3xl">
          <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight mb-6">
            Études de Cas <span className="text-primary">Analysées</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Nous ne nous contentons pas de communiquer, nous performons. Découvrez comment nos stratégies sur mesure transforment les défis en résultats mesurables pour nos partenaires B2B.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {caseStudies.map((study, i) => <Card key={i} data-index={i} className="bg-card/50 border-border/50 backdrop-blur-sm hover:border-primary/30 transition-colors duration-300 flex flex-col">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-primary uppercase tracking-wider">
                    {study.client}
                  </span>
                  <study.icon className="w-5 h-5 text-muted-foreground" />
                </div>
                <CardTitle className="text-xl md:text-2xl font-sans">
                  {study.title}
                </CardTitle>
                <CardDescription className="text-muted-foreground mt-2 line-clamp-2">
                  {study.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-1 pb-2">
                <div className="mb-8">
                  <div className="text-4xl md:text-5xl font-bold text-primary mb-1 tracking-tight">
                    {study.metric}
                  </div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                    {study.metricLabel}
                  </div>
                </div>

                <div className="h-[180px] w-full mt-auto">
                  <ChartContainer config={study.chartConfig} className="h-full w-full">
                    {study.chartType === "area" ? <AreaChart data={study.chartData} margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0
                }}>
                        <defs>
                          <linearGradient id={`fillGrowth-${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--color-value)" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="var(--color-value)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={value => value.slice(0, 3)} stroke="var(--muted-foreground)" fontSize={12} />
                        <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" hideLabel />} />
                        <Area dataKey="value" type="natural" fill={`url(#fillGrowth-${i})`} fillOpacity={0.4} stroke="var(--color-value)" strokeWidth={2} />
                      </AreaChart> : <BarChart data={study.chartData} margin={{
                  top: 10,
                  right: 10,
                  left: 0,
                  bottom: 0
                }}>
                        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                        <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} stroke="var(--muted-foreground)" fontSize={12} />
                        <ChartTooltip cursor={{
                    fill: "var(--muted)",
                    opacity: 0.1
                  }} content={<ChartTooltipContent indicator="line" hideLabel />} />
                        <Bar dataKey="value" fill="var(--color-value)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                      </BarChart>}
                  </ChartContainer>
                </div>
              </CardContent>
              
              <CardFooter className="pt-4 border-t border-border/30">
                <Link to="/ProjectsPage" className="w-full">
                  <Button variant="ghost" className="w-full justify-between group hover:bg-primary/10 hover:text-primary">
                    Voir l'étude complète
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </CardFooter>
            </Card>)}
        </div>

        {}
        <div className="mt-16 text-center">
          <Link to="/ProjectsPage">
            <Button size="lg" variant="default" className="font-semibold px-8">
              Explorer toutes nos réalisations
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}